<template>
  <b-modal :id="modal_id" :title="titles[currentStep]" size="lg">
    <div v-if="!isValid.customer">
      <b-alert v-if="validation && validation.customer_expired" show>Your company information is expired, please review and save to continue.</b-alert>
      <b-alert v-if="validation && validation.customer_incomplete.length" show v-html="validation ? validation.customer_incomplete.join('<br />') : ''"></b-alert>
      <customer-form :customer="form.customer" @onSaved="savedCustomer" save_label="Save and Continue"></customer-form>
    </div>
    <div v-else-if="!isValid.shipping_address || !isValid.billing_address">
      <b-alert v-if="validation && (validation.shipping_address_expired || validation.billing_address_expired)" show>Your company address is expired, please review and save to continue.</b-alert>
      <b-alert v-if="validation && validation.shipping_address_incomplete.length" show v-html="validation ? validation.shipping_address_incomplete.join('<br />') : ''"></b-alert>
      <b-alert v-if="validation && validation.billing_address_incomplete.length" show v-html="validation ? validation.billing_address_incomplete.join('<br />') : ''"></b-alert>
      <customer-location-form :shipping_address="form.shipping_address" :billing_address="form.billing_address" @change="fillAddresses"></customer-location-form>
      <div class="clearfix"><div class="d-inline-block float-right">
        <b-overlay :show="saving" spinner-small>
            <b-button squared variant="sf" @click="saveAddresses">Save and Continue</b-button>
        </b-overlay>
      </div></div>
    </div>
    <div v-else-if="!isValid.accounting_contact">
      <b-alert v-if="validation && validation.accounting_contact_expired" show>Your accounting contact is expired, please review and save to continue.</b-alert>
      <b-alert v-if="validation && validation.accounting_contact_incomplete.length" show v-html="validation ? validation.accounting_contact_incomplete.join('<br />') : ''"></b-alert>
      <contact-edit-form :contact="form.accounting_contact" :hide_fields="{type:true}" :commit="false" @change="i=>(form.accounting_contact={...form.accounting_contact,...i})"></contact-edit-form>
      <div class="clearfix"><div class="d-inline-block float-right">
        <b-overlay :show="saving" spinner-small>
            <b-button squared variant="sf" @click="saveAccountingContact">Save and Continue</b-button>
        </b-overlay>
      </div></div>
    </div>
    <div v-else-if="!isValid.customer_contacts">
      <div v-for="(customer_contact,k) in validation.customer_contacts" :key="'cc_update_'+k">
        <div v-if="!customer_contact.contact_valid || !customer_contact.user_valid">
          <hr />
          <b-alert v-if="customer_contact.contact_expired || customer_contact.user_expired" show>This contact is expired.</b-alert>
          <b-alert v-if="customer_contact.contact_incomplete.length" show v-html="customer_contact ? customer_contact.contact_incomplete.join('<br />') : ''"></b-alert>
          <b-alert v-if="customer_contact.user_incomplete.length" show v-html="customer_contact ? customer_contact.user_incomplete.join('<br />') : ''"></b-alert>
          <contact-edit-form 
            :contact="customer_contact.contact"
            :commit="false" 
            @change="fillCustomerContact(k, $event)">
          </contact-edit-form>
          <div v-if="customer_contact.user" class="border-left pl-2">
            <c-select
              label="Notification Preference"
              :value="customer_contact.user.notification_preference"
              @update:value="updateNotificationPreference(k, $event)"
              :options="notification_options"
              :horizontal="true"
              required
              :addLabelClasses="['font-weight-bold']"
            ></c-select>
          </div>
          <location-edit-form :location="customer_contact.location" @change="fillCustomerContactLocation(k, $event)" :commit="false"></location-edit-form>
        </div>
      </div>
      <div class="clearfix"><div class="d-inline-block float-right">
        <b-overlay :show="saving" spinner-small>
            <b-button squared variant="sf" @click="saveCustomerContacts">Save and Continue</b-button>
        </b-overlay>
      </div></div>
    </div>
    <template v-slot:modal-footer="{}">
      <span></span>
    </template>
  </b-modal>
</template>

<script>
import Customer from '../../../models/Customer';
import CustomerForm from '../CustomerForm.vue';
import CustomerLocationForm from '../locations/CustomerLocationForm.vue';
import CustomerLocationRepository from '@/repositories/CustomerLocationRepository';
import CustomerContactRepository from '@/repositories/CustomerContactRepository';
import CustomerContactForm from '../contacts/CustomerContactForm.vue';
import ContactEditForm from '../contacts/ContactEditForm.vue';
import LocationEditForm from '../locations/LocationEditForm.vue';

export default {
  name: 'CustomerValidationModal',
    components: {CustomerForm, CustomerLocationForm, CustomerContactForm, ContactEditForm, LocationEditForm},
    props: ['modal_id'],
    data() {
    return {
      validation: null,
      saving: false,
      form: {
        customer: null,
        shipping_address: null,
        billing_address: null,
        accounting_contact: null,
        customer_contacts: []
      },
      isValid: {
        customer: false,
        shipping_address: false,
        billing_address: false,
        accounting_contact: false,
        customer_contacts: false
      },
      titles: {
        customer: 'Looks like your company information needs updated!',
        shipping_address: 'Looks like your company address needs updated!',
        billing_address: 'Looks like your company address needs updated!',
        accounting_contact: 'Looks like your accounting contact needs updated!',
        customer_contacts: 'Looks like your contacts need to to be updated!'
      },
      notification_options: [
        {value: null, label: '- Select -'},
        {value: 'mail', label: 'Email'},
        {value: 'sms', label: 'SMS'},
        {value: 'both', label: 'Both'},
        {value: 'unsubscribe', label: 'Unsubscribe'}
      ]
    };
  },
  computed: {
    currentStep() {
      return Object.keys(this.isValid).filter(k=>this.isValid[k]===false).find(()=>true);
    },
    lastStep() {
      return Object.keys(this.isValid).filter(k=>this.isValid[k]===false).slice(-1).pop();
    },
  },
  methods: {
    checkValidation() {
      if(Object.keys(this.isValid).filter(k=>this.isValid[k]===false).length === 0) {
        this.$bvModal.hide(this.modal_id);
      }
    },
    savedCustomer(customer) {
      this.form.customer = customer;
      this.isValid.customer = true;
      this.checkValidation();
    },
    fillAddresses(payload) {
      let billing = payload.billing_address;
      let keys = ['street_address','city','region','country','postal'];
      let shipping_address = Object.fromEntries(keys.map(k=>[k,payload[k]]));
      let billing_address = Object.fromEntries(keys.map(k=>[k,payload[k]]));
      billing_address.street_address = billing;
      shipping_address.name = 'Shipping Address';
      billing_address.name = 'Billing Address';
      this.form.shipping_address = {...this.form.shipping_address, ...shipping_address};
      this.form.billing_address = {...this.form.billing_address, ...billing_address};
    },
    saveAddresses() {
      this.saving = true;
      let customer = Customer.find(this.$store.getters.getAuthCustomerID);
      return Promise.all([
        CustomerLocationRepository.updateOrCreateLocation(customer, this.form.shipping_address),
        CustomerLocationRepository.updateOrCreateLocation(customer, this.form.billing_address)
      ]).then(() => {
          this.saving = false;
          this.isValid.shipping_address = true;
          this.isValid.billing_address = true;
          this.checkValidation();
      });
    },
    saveAccountingContact() {
      this.saving = true;
      let customer = Customer.find(this.$store.getters.getAuthCustomerID);
      return CustomerContactRepository.updateOrCreateContact(customer, {...this.form.accounting_contact, type: 'Accounting'}).then(() => {
          this.saving = false;
          this.isValid.accounting_contact = true;
          this.checkValidation();
      });
    },
    fillCustomerContact(k, payload) {
      let index = this.form.customer_contacts.findIndex((i)=>i.key===k);
      if(index !== -1) {
        this.form.customer_contacts.splice(index, 1, {...this.form.customer_contacts[index], ...{contact: payload}});
      } else {
        this.form.customer_contacts.push({key:k, contact: payload});
      }
    },
    updateNotificationPreference(k, pref) {
      let index = this.form.customer_contacts.findIndex((i)=>i.key===k);
      if(index !== -1) {
        this.form.customer_contacts.splice(index, 1, {...this.form.customer_contacts[index], ...{user: {notification_preference: pref}}});
      } else {
        this.form.customer_contacts.push({key:k, user: {notification_preference: pref}});
      }
    },
    fillCustomerContactLocation(k, payload) {
      let index = this.form.customer_contacts.findIndex((i)=>i.key===k);
      if(index !== -1) {
        this.form.customer_contacts.splice(index, 1, {...this.form.customer_contacts[index], ...{location: payload}});
      } else {
        this.form.customer_contacts.push({key:k, location: payload});
      }
    },
    saveCustomerContacts() { 
      this.saving = true;
      let customer = Customer.find(this.$store.getters.getAuthCustomerID);
      let requests = [];
      this.form.customer_contacts.forEach((i) => {
        console.log(contact);
        let cc = this.validation.customer_contacts.find((j,k)=>k===i.key);
        let contact = {...cc.contact, ...i.contact};
        let location = {...(cc.location?cc.location:{}), ...i.location};
        let user = null;
        if(cc.user) {
          user = {...cc.user, ...i.user};
        }
        requests.push(CustomerContactRepository.updateOrCreateContact(customer, contact, user, location));
      });
      return Promise.all(requests).then(() => {
        this.saving = false;
        this.isValid.customer_contacts = true;
        this.checkValidation();
      });
    }
  },
  async mounted() {}
}
</script>
